import { PIPE_TYPES, PIPE_CONNECTIONS, GRID_SIZE } from "../constants/pipes";
import { getEndpointPipeType } from "./puzzleGenerator";

// Get connections for a rotated pipe
export const getConnectionsForRotation = (type, rotation) => {
  if (!type || !PIPE_CONNECTIONS[type]) {
    return [];
  }

  const baseConnections = PIPE_CONNECTIONS[type];
  const rotationSteps = rotation / 90;

  const dirMap = {
    top: ["top", "right", "bottom", "left"],
    right: ["right", "bottom", "left", "top"],
    bottom: ["bottom", "left", "top", "right"],
    left: ["left", "top", "right", "bottom"],
  };

  return baseConnections.map((dir) => {
    const dirArray = dirMap[dir];
    return dirArray[rotationSteps % 4];
  });
};

// Check if two pipes are connected
export const arePipesConnected = (pipe1, pipe2, direction) => {
  if (!pipe1 || !pipe2 || !pipe1.type || !pipe2.type) {
    return false;
  }

  const pipe1Connections = getConnectionsForRotation(
    pipe1.type,
    pipe1.rotation
  );
  const pipe2Connections = getConnectionsForRotation(
    pipe2.type,
    pipe2.rotation
  );

  const oppositeDirection = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right",
  }[direction];

  // For endpoints, we only need to check the connection in the correct direction
  if (pipe1.isEndpoint) {
    const endpointType = getEndpointPipeType(pipe1.x, pipe1.y);
    const expectedConnection = {
      [PIPE_TYPES.END_TOP]: "bottom",
      [PIPE_TYPES.END_RIGHT]: "left",
      [PIPE_TYPES.END_BOTTOM]: "top",
      [PIPE_TYPES.END_LEFT]: "right",
    }[endpointType];

    return (
      direction === expectedConnection &&
      pipe2Connections.includes(oppositeDirection)
    );
  }

  if (pipe2.isEndpoint) {
    const endpointType = getEndpointPipeType(pipe2.x, pipe2.y);
    const expectedConnection = {
      [PIPE_TYPES.END_TOP]: "bottom",
      [PIPE_TYPES.END_RIGHT]: "left",
      [PIPE_TYPES.END_BOTTOM]: "top",
      [PIPE_TYPES.END_LEFT]: "right",
    }[endpointType];

    return (
      oppositeDirection === expectedConnection &&
      pipe1Connections.includes(direction)
    );
  }

  return (
    pipe1Connections.includes(direction) &&
    pipe2Connections.includes(oppositeDirection)
  );
};

// Find start and end points in the grid
export const findStartAndEndPoints = (grid) => {
  let start = null;
  let end = null;

  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (grid[y][x].isEndpoint) {
        if (start === null) {
          start = { x, y };
        } else {
          end = { x, y };
        }
      }
    }
  }

  return { start, end };
};

// Check if the puzzle is solved
export const checkSolution = (grid) => {
  const { start, end } = findStartAndEndPoints(grid);
  if (!start || !end) return false;

  const visited = new Set();
  const queue = [{ x: start.x, y: start.y, fromDirection: null }];
  visited.add(`${start.x},${start.y}`);

  while (queue.length > 0) {
    const current = queue.shift();

    // Check if we reached the end point
    if (current.x === end.x && current.y === end.y) {
      return true;
    }

    // Check all four directions
    const directions = [
      { dx: 0, dy: -1, dir: "top" },
      { dx: 1, dy: 0, dir: "right" },
      { dx: 0, dy: 1, dir: "bottom" },
      { dx: -1, dy: 0, dir: "left" },
    ];

    for (const { dx, dy, dir } of directions) {
      const newX = current.x + dx;
      const newY = current.y + dy;

      if (
        newX >= 0 &&
        newX < GRID_SIZE &&
        newY >= 0 &&
        newY < GRID_SIZE &&
        !visited.has(`${newX},${newY}`)
      ) {
        const currentPipe = grid[current.y][current.x];
        const nextPipe = grid[newY][newX];

        // Skip if we're trying to go back the way we came
        if (current.fromDirection && dir === current.fromDirection) {
          continue;
        }

        if (arePipesConnected(currentPipe, nextPipe, dir)) {
          queue.push({
            x: newX,
            y: newY,
            fromDirection: {
              top: "bottom",
              right: "left",
              bottom: "top",
              left: "right",
            }[dir],
          });
          visited.add(`${newX},${newY}`);
        }
      }
    }
  }

  return false;
};
